import React, { useEffect, useState, useRef } from 'react';
import { Space, Form, Row } from 'antd';
import { Link } from 'react-router-dom';
import { type } from '../../utilities/constants';
import {
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAGE_TITLE,
  searchStaticProps,
} from './constants';
import Search from './components/Search/Search';
import LearnerSocialMediaTable from './components/LearnerSocialMediaTable/LearnerSocialMediaTable';
import { getLearnersSocialMediaData } from '../../services/learnerService';
import DailyFollowersChart from './components/DailyFollowersChart/DailyFollowersChart';
import { formatSocialMediaData, getChartDataFormat } from './utils';
import style from './style.module.css';

const LearnerSocialMediaPageContainer = () => {
  const [form] = Form.useForm();
  const totalCount = useRef(null);
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: PAGE_SIZE,
  });
  const [status, setStatus] = useState(false);
  const [socialMediaData, setSocialMediaData] = useState([]);
  const [totalDailyLogs, setTotalDailyLogs] = useState([]);
  const [activeItem, setActiveItem] = useState(null);

  const fetchData = (pageNo, pageSize, param) => {
    setStatus('LOADING');
    getLearnersSocialMediaData(pageNo, pageSize, param)
      .then((response) => {
        totalCount.current = response.data.totalCount;
        setSocialMediaData(response.data.paginatedList);
        setTotalDailyLogs(response.data.totalDailyLogs);
        setStatus('SUCCESS');
      })
      .catch((error) => {
        console.log('Error getting data from the admin backend:', error);
        alert('Getting data from the backend failed!');
        setStatus('ERROR');
      });
  };

  useEffect(() => {
    fetchData(pagination.pageNo, pagination.pageSize);
  }, [pagination]);

  const onSearch = (data) => {
    fetchData(1, PAGE_SIZE, { [data.param]: data.val });
  };

  const onPaginationChange = (data) => {
    setPagination({
      pageNo: data.current,
      pageSize: data.pageSize,
    });
  };

  const onReset = () => {
    form.resetFields();
    fetchData(1, PAGE_SIZE);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'learnerId',
      key: 'learnerId',
      render: (text, record) => (
        <Space size="middle">
          <Link to={'/learner/' + record?.learnerId}>{record?.learnerId}</Link>
        </Space>
      ),
      type: type.NUMBER,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (text, record) => (
        <div onClick={() => setActiveItem(record.learnerId)}>
          {record.firstName}
        </div>
      ),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (text, record) => (
        <div onClick={() => setActiveItem(record.learnerId)}>
          {record.lastName}
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => (
        <div onClick={() => setActiveItem(record.learnerId)}>
          {record.email}
        </div>
      ),
    },
    {
      title: 'Facebook followers',
      dataIndex: 'facebookFollowers',
      key: 'facebookFollowers',
      type: type.NUMBER,
      render: (text, record) => {
        const formattedValue =
          typeof record?.facebookFollowers === 'number'
            ? new Intl.NumberFormat('en-US').format(record.facebookFollowers)
            : record?.facebookFollowers;
        return (
          <div onClick={() => setActiveItem(record.learnerId)}>
            {formattedValue}
          </div>
        );
      },
    },
    {
      title: 'Instagram followers',
      dataIndex: 'instagramFollowers',
      key: 'instagramFollowers',
      type: type.NUMBER,
      render: (text, record) => {
        const formattedValue =
          typeof record?.instagramFollowers === 'number'
            ? new Intl.NumberFormat('en-US').format(record.instagramFollowers)
            : record?.instagramFollowers;
        return (
          <div onClick={() => setActiveItem(record.learnerId)}>
            {formattedValue}
          </div>
        );
      },
    },
    {
      title: 'Total followers',
      dataIndex: 'totalFollowers',
      key: 'totalFollowers',
      type: type.NUMBER,
      render: (text, record) => {
        console.log(record.totalFollowers);
        const formattedValue =
          typeof record?.totalFollowers === 'number'
            ? new Intl.NumberFormat('en-US').format(record.totalFollowers)
            : record?.totalFollowers;
        return (
          <div onClick={() => setActiveItem(record.learnerId)}>
            {formattedValue}
          </div>
        );
      },
    },
  ];

  const getActiveItemData = () => {
    const activeItemData = socialMediaData.find(
      (e) => e.learnerData.learnerId === activeItem
    );
    return !activeItem ? [] : getChartDataFormat(activeItemData.dailyLogs);
  };

  return (
    <div className={style.general}>
      <Space size="large">
        <h1>{PAGE_TITLE}</h1>
      </Space>
      <Row justify="space-between">
        <h3>Total {totalCount.current} Record(s)</h3>
      </Row>
      <Search
        form={form}
        onSearch={onSearch}
        onReset={onReset}
        staticProps={searchStaticProps}
      />
      <Row>
        <DailyFollowersChart data={getActiveItemData()} />
        <DailyFollowersChart data={getChartDataFormat(totalDailyLogs)} />
      </Row>
      <LearnerSocialMediaTable
        loading={status === 'LOADING'}
        data={formatSocialMediaData(socialMediaData)}
        columns={columns}
        totalCount={totalCount.current}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPaginationChange={onPaginationChange}
      />
    </div>
  );
};

export default LearnerSocialMediaPageContainer;
